import React, { useEffect, useState } from 'react';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { CategoryDeleteWarningStatus } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { Guid } from '@Shared.Angular/@types/guid';
import { ISubCategoryFlowModel } from '@Shared.Angular/flowingly.services/category.api.service';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import SubCategoryAddModal from '../Modals/SubCategoryAddModal';
import CategoryAddEditModal from '../Modals/CategoryAddEditModal';
import CategoryDeleteModal from '../Modals/CategoryDeleteModal';

type Props = {
  id: Guid;
  categoryName: string;
  description: string;
  approverId?: Guid;
  flowModelEditors?: IFlowinglySearchResult[];
  parentId: Guid;
  numberOfFlows: number;
  level: number;
  onCategoryChange: () => Promise<void>;
};

const CategoryListActions = (props: Props) => {
  const {
    id,
    categoryName,
    description,
    approverId,
    flowModelEditors,
    parentId,
    numberOfFlows,
    level,
    onCategoryChange
  } = props;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] =
    useState<CategoryDeleteWarningStatus>();
  const [subCategoriesToBeDeleted, setSubCategoriesToBeDeleted] = useState<
    ISubCategoryFlowModel[]
  >([]);
  const [procedureCount, setProcedureCount] = useState<number>(0);

  const categoryApiService =
    useService<SharedAngular.CategoryApiService>('categoryApiService');

  const appInsightsService =
    useService<SharedAngular.AppInsightsService>('appInsightsService');

  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');

  const [maxSubCategories, setMaxSubCategories] = useState(
    appConfig.maxSubCategories
  );

  const isSubcategoryAllowed = level < maxSubCategories;

  useEffect(() => {
    setMaxSubCategories(appConfig.maxSubCategories);
  }, []);

  const handleEditClose = () => setIsEditModalOpen(false);
  const handleAddClose = () => setIsAddModalOpen(false);
  const handleDeleteClose = () => setIsDeleteModalOpen(false);

  const editClick = () => {
    setIsEditModalOpen(true);
  };

  const deleteClick = async () => {
    try {
      appInsightsService.startEventTimer('subcategoryDeleteWarning');
      const response = await categoryApiService.getSubCategoryWarning(id);
      setDeleteStatus(response.status);
      setSubCategoriesToBeDeleted(response.subCategoryFlowModels);
      setIsDeleteModalOpen(true);
      setProcedureCount(response.procedureCount);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const addSubCategory = async (event) => {
    setIsAddModalOpen(true);
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-evenly'
      }}
    >
      <span
        onClick={addSubCategory}
        title="Add Subcategory"
        className={`list-actions__action ${
          isSubcategoryAllowed ? '' : 'hidden-button'
        }`}
      >
        <i className="fa-light fa-plus" id={`add-subcategory-button-${id}`}></i>
      </span>

      <span
        onClick={editClick}
        title="Edit Category"
        className="list-actions__action"
      >
        <i
          className="fa-light fa-pencil"
          id={`edit-subcategory-button-${id}`}
        ></i>
      </span>

      {isAddModalOpen && (
        <SubCategoryAddModal
          parentId={id}
          closeModal={handleAddClose}
          onSubCategoryAdded={onCategoryChange}
        />
      )}

      {isEditModalOpen && (
        <CategoryAddEditModal
          categoryId={id}
          categoryName={categoryName}
          description={description || ''}
          approverId={approverId}
          parentId={parentId || null}
          flowModelEditors={flowModelEditors || []}
          closeModal={handleEditClose}
          onCategoryChange={onCategoryChange}
        />
      )}
      <span
        onClick={deleteClick}
        title="Delete Category"
        className="list-actions__action"
      >
        <i
          className="fa-light fa-trash-can"
          id={`delete-subcategory-button-${id}`}
        ></i>
      </span>
      {isDeleteModalOpen && (
        <CategoryDeleteModal
          deleteStatus={deleteStatus}
          categoryName={categoryName}
          flowModelCount={numberOfFlows}
          procedureCount={procedureCount}
          id={id}
          subCategoriesToBeDeleted={subCategoriesToBeDeleted}
          closeModal={handleDeleteClose}
          onCategoryDeleted={onCategoryChange}
        />
      )}
    </div>
  );
};

export default CategoryListActions;
